import { create } from 'zustand';

export type JobsListView = 'card' | 'table';

export interface JobsListSort {
  label?: string;
  value?: string;
  element?: string;
  order?: string;
}

export interface JobsListFilters {
  office_ids?: unknown[];
  department_ids: unknown[];
  job_statuses?: unknown[];
  user_ids: unknown[];
  eeoc_data_collection: unknown[];
  isFilterApplied: boolean;
  /** New filter format used by the revamped filter bar. */
  feConfig: any[];
  /** Backend-shaped filter payload — carries condition objects like `{ is: [392] }`. */
  beConfig: Record<string, any>;
  [key: string]: any;
}

// A type alias, not an interface: the shared Table's `page` prop carries an
// index signature, which an interface never satisfies structurally.
export type JobsListPage = {
  number: number;
  offset: number;
  limit: number;
};

const DEFAULT_SORT: JobsListSort = {
  label: 'Created date (Newest to oldest)',
  value: 'DESC',
  element: 'created_at',
  order: 'DESC',
};

const DEFAULT_FILTERS: JobsListFilters = {
  office_ids: undefined,
  department_ids: [],
  job_statuses: undefined,
  user_ids: [],
  eeoc_data_collection: [],
  isFilterApplied: false,
  feConfig: [],
  beConfig: {},
};

const DEFAULT_PAGE: JobsListPage = { number: 0, offset: 0, limit: 20 };

const hasActiveFilters = (filters: Partial<JobsListFilters> = {}) =>
  Object.keys(filters.beConfig || {}).length > 0 ||
  (filters.feConfig || []).length > 0;

interface JobsListState {
  searchTerm: string | null;
  filters: JobsListFilters;
  page: JobsListPage;
  sort: JobsListSort;
  currentView: JobsListView;

  setCurrentView: (_view: JobsListView) => void;
  applySearch: (_searchTerm: string | null) => void;
  applySort: (_sort: JobsListSort) => void;
  applyPage: (_page: number) => void;
  applyFilter: (_payload: {
    key?: string;
    value?: unknown;
    feConfig?: any[];
    beConfig?: Record<string, any>;
    isNewFilter?: boolean;
  }) => void;
  setFiltersData: (_feConfig: any[]) => void;
  clearFilter: (_key: string) => void;
  clearAllFilter: () => void;
  clearData: () => void;
}

/**
 * View state for the jobs list — search, filters, sort, paging and the
 * card/table toggle. Replaces the client half of the `atsJobs` Redux slice; the
 * jobs themselves come from `useJobsList`, keyed on this.
 *
 * The action semantics mirror `atsJobsReducer` exactly, including the parts
 * that look inconsistent: `applySort` deliberately does NOT reset the page,
 * while every filter/search change does.
 */
export const useJobsListStore = create<JobsListState>((set) => ({
  searchTerm: null,
  filters: DEFAULT_FILTERS,
  page: DEFAULT_PAGE,
  sort: DEFAULT_SORT,
  currentView: 'card',

  setCurrentView: (view) => set({ currentView: view }),

  applySearch: (searchTerm) =>
    set((state) => ({
      searchTerm,
      page: { ...DEFAULT_PAGE },
      // Emptying the search box must not read as "no filters" — that wipes the
      // user's stored filters and shows the create-your-first-job empty state.
      filters: {
        ...state.filters,
        isFilterApplied:
          (searchTerm?.length ?? 0) > 0 || hasActiveFilters(state.filters),
      },
    })),

  // Matches the reducer: sorting does not reset paging.
  applySort: (sort) => set({ sort }),

  applyPage: (number) =>
    set((state) => ({
      page: { ...state.page, number, offset: number * state.page.limit },
    })),

  applyFilter: ({ key, value, feConfig, beConfig, isNewFilter }) =>
    set((state) => {
      if (isNewFilter) {
        return {
          filters: {
            ...state.filters,
            ...(feConfig ? { feConfig } : {}),
            ...(beConfig ? { beConfig } : {}),
            isFilterApplied: true,
          },
          page: { ...DEFAULT_PAGE },
        };
      }
      return {
        filters: {
          ...state.filters,
          ...(key ? { [key]: value } : {}),
          isFilterApplied: true,
        },
        page: { ...DEFAULT_PAGE },
      };
    }),

  setFiltersData: (feConfig) =>
    set((state) => ({
      filters: { ...state.filters, feConfig, isFilterApplied: true },
      page: { ...DEFAULT_PAGE },
    })),

  clearFilter: (key) =>
    set((state) => {
      const beConfig = { ...(state.filters.beConfig || {}) };
      delete beConfig[key];
      const next: JobsListFilters = {
        ...state.filters,
        [key]: DEFAULT_FILTERS[key],
        beConfig,
        feConfig: (state.filters.feConfig || []).filter(
          (item) => item?.filter_by?.value !== key
        ),
      };
      return {
        filters: {
          ...next,
          isFilterApplied:
            hasActiveFilters(next) || (state.searchTerm?.length ?? 0) > 0,
        },
        page: { ...DEFAULT_PAGE },
      };
    }),

  clearAllFilter: () =>
    set({
      filters: { ...DEFAULT_FILTERS, feConfig: [], beConfig: {} },
      page: { ...DEFAULT_PAGE },
    }),

  clearData: () =>
    set({ sort: { ...DEFAULT_SORT }, currentView: 'card', searchTerm: '' }),
}));

// Narrow subscriptions. Selecting the whole store re-renders every consumer on
// any change — the problem the getAtsJobs* selector split fixed in #6307.
export const useJobsListSearchTerm = () =>
  useJobsListStore((s) => s.searchTerm);
export const useJobsListFilters = () => useJobsListStore((s) => s.filters);
export const useJobsListPage = () => useJobsListStore((s) => s.page);
export const useJobsListSort = () => useJobsListStore((s) => s.sort);
export const useJobsListView = () => useJobsListStore((s) => s.currentView);
